import { create } from 'zustand'
import { Settings, UpdateSettingsDto, EquipmentConfig, ServerConfig, Certificates } from '@/types'
import { settingsService } from '@/services/settings.service'
import { groupsService } from '@/services/groups.service'

export interface GroupSettings {
  equipmentConfig?: EquipmentConfig | null
  serverConfig?: ServerConfig | null
  certificates?: Certificates | null
}

interface SettingsState {
  settings: Settings | null
  groupSettings: GroupSettings | null
  selectedGroupId: string | null
  isLoading: boolean
  isGroupSettingsLoading: boolean
  isInitialized: boolean
  fetchSettings: () => Promise<void>
  fetchGroupSettings: (groupId: string) => Promise<void>
  setSelectedGroupId: (groupId: string | null) => void
  updateSettings: (data: UpdateSettingsDto) => Promise<void>
  updateEquipmentConfig: (groupId: string, config: EquipmentConfig) => Promise<EquipmentConfig | null>
  updateServerConfig: (groupId: string, config: ServerConfig) => Promise<ServerConfig | null>
  updateCertificates: (groupId: string, certificates: Certificates) => Promise<Certificates | null>
  uploadRegime: (file: File) => Promise<void>
  downloadRegime: () => Promise<void>
}

export const useSettingsStore = create<SettingsState>((set, get) => {
  let fetchPromise: Promise<void> | null = null
  let fetchGroupSettingsPromise: Promise<void> | null = null
  let fetchGroupSettingsId: string | null = null

  return {
    settings: null,
    groupSettings: null,
    selectedGroupId: null,
    isLoading: false,
    isGroupSettingsLoading: false,
    isInitialized: false,

    fetchSettings: async () => {
      if (get().isInitialized) {
        return
      }

      if (fetchPromise) {
        return fetchPromise
      }

      fetchPromise = (async () => {
        set({ isLoading: true })
        try {
          const settings = await settingsService.getSettings()
          set({ settings, isInitialized: true })
        } catch (error) {
          console.error('Error fetching settings:', error)
          throw error
        } finally {
          set({ isLoading: false })
          fetchPromise = null
        }
      })()

      return fetchPromise
    },

    fetchGroupSettings: async (groupId: string) => {
      if (fetchGroupSettingsPromise && fetchGroupSettingsId === groupId) {
        return fetchGroupSettingsPromise
      }

      fetchGroupSettingsId = groupId
      fetchGroupSettingsPromise = (async () => {
        set({ isGroupSettingsLoading: true, selectedGroupId: groupId })
        try {
          const [equipmentConfig, serverConfig, certificates] = await Promise.all([
            groupsService.getEquipmentConfig(groupId),
            groupsService.getServerConfig(groupId),
            groupsService.getCertificates(groupId),
          ])
          set({
            groupSettings: { equipmentConfig, serverConfig, certificates },
          })
        } catch (error) {
          console.error('Error fetching group settings:', error)
          throw error
        } finally {
          set({ isGroupSettingsLoading: false })
          fetchGroupSettingsPromise = null
          fetchGroupSettingsId = null
        }
      })()

      return fetchGroupSettingsPromise
    },

    setSelectedGroupId: (groupId: string | null) => {
      set({ selectedGroupId: groupId })
    },

    updateSettings: async (data: UpdateSettingsDto) => {
      try {
        const updatedSettings = await settingsService.updateSettings(data)
        set({ settings: updatedSettings })
      } catch (error) {
        console.error('Error updating settings:', error)
        throw error
      }
    },

    updateEquipmentConfig: async (groupId: string, config: EquipmentConfig) => {
      try {
        const updatedConfig = await groupsService.updateEquipmentConfig(groupId, config)
        set(state => ({
          groupSettings: {
            ...(state.groupSettings || {}),
            equipmentConfig: updatedConfig,
          },
        }))
        return updatedConfig
      } catch (error) {
        console.error('Error updating equipment config:', error)
        throw error
      }
    },

    updateServerConfig: async (groupId: string, config: ServerConfig) => {
      try {
        const updatedConfig = await groupsService.updateServerConfig(groupId, config)
        set(state => ({
          groupSettings: {
            ...(state.groupSettings || {}),
            serverConfig: updatedConfig,
          },
        }))
        return updatedConfig
      } catch (error) {
        console.error('Error updating server config:', error)
        throw error
      }
    },

    updateCertificates: async (groupId: string, certificates: Certificates) => {
      try {
        const updatedCertificates = await groupsService.updateCertificates(groupId, certificates)
        set(state => ({
          groupSettings: {
            ...(state.groupSettings || {}),
            certificates: updatedCertificates,
          },
        }))
        return updatedCertificates
      } catch (error) {
        console.error('Error updating certificates:', error)
        throw error
      }
    },

    uploadRegime: async (file: File) => {
      set({ isLoading: true })
      try {
        await settingsService.uploadRegime(file)
        set({ isInitialized: false })
        await get().fetchSettings()
      } catch (error) {
        console.error('Error uploading regime file:', error)
        throw error
      } finally {
        set({ isLoading: false })
      }
    },

    downloadRegime: async () => {
      try {
        const settings = get().settings
        const regime = settings?.regime

        if (!regime?.data) {
          throw new Error('Aucun fichier régime disponible')
        }

        const binaryString = atob(regime.data)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }

        const blob = new Blob([bytes], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = regime.filename || `regime-${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error downloading regime file:', error)
        throw error
      }
    },
  }
})
